import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock3, Loader2, Mail, Power, Repeat2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCampaignAutomaticImport, type CampaignAutomaticImportOptions } from '@/hooks/use-campaign-automatic-import';
import { canImportCampaign } from '@/lib/campaigns/socialImport';
import { YouTubeDiscoveryNotice } from './YouTubeDiscoveryNotice';

export function CampaignAutomaticImportControl(props: CampaignAutomaticImportOptions) {
  const { t } = useTranslation();
  const id = useId();
  const state = useCampaignAutomaticImport(props);
  const disabled = props.disabled || state.sessionChanged || !canImportCampaign(props.campaign, props.acceptedTerms);
  const known = state.automaticImport !== null;
  const enabled = state.automaticImport === true;
  const pending = state.preferenceLoading || state.preferenceSaving;
  const pulseInactive = known && !enabled && !disabled && !pending && !state.preferenceError;
  const accent = enabled ? 'text-primary' : 'text-muted-foreground';
  // The action already says it is checking or saving; the badge keeps the saved
  // state and is left out while that state is still being read.
  const status = !known ? (state.preferenceLoading ? null : 'automaticUnknown')
    : enabled ? 'automaticOn' : 'automaticOff';
  const action = state.preferenceSaving ? 'preferenceSaving' : state.preferenceLoading ? 'preferenceLoading'
    : !known ? 'automaticUnavailable' : enabled ? 'automaticDeactivate' : 'automaticActivate';
  return <section aria-labelledby={`${id}-automatic-title`} aria-busy={pending}
    className={`rounded-xl border border-l-4 p-4 sm:p-5 ${enabled ? 'border-primary/60 border-l-primary bg-primary/10' : 'border-border border-l-muted-foreground/50 bg-muted/30'}`}>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${enabled ? 'border-primary/20 bg-primary/15 text-primary' : 'border-border bg-muted text-muted-foreground'}`}>
          <Repeat2 className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={`${id}-automatic-title`} className={`text-base font-semibold sm:text-lg ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{t('campaign.socialImport.automaticLabel')}</h3>
            {status && <Badge id={`${id}-automatic-status`} variant="outline" aria-live="polite" className={enabled ? 'border-primary/40 text-primary' : 'border-border bg-background/60 text-muted-foreground'}>
              {t(`campaign.socialImport.${status}`)}
            </Badge>}
          </div>
          <p id={`${id}-automatic-description`} className={`text-sm leading-relaxed ${enabled ? 'text-foreground/85' : 'text-muted-foreground'}`}>{t('campaign.socialImport.automaticDescription')}</p>
        </div>
      </div>
      <Button type="button" role="switch" aria-checked={enabled}
        aria-labelledby={`${id}-automatic-title`} aria-describedby={`${id}-automatic-description ${id}-automatic-help ${id}-automatic-status`}
        variant={enabled || !known ? 'outline' : 'secondary'} className={`h-auto min-h-11 w-full shrink-0 whitespace-normal px-4 py-2.5 sm:w-auto ${pulseInactive ? 'campaign-automatic-import-inactive-pulse' : ''}`}
        disabled={disabled || pending || !known} onClick={() => void state.saveAutomaticImport(!enabled)}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Power className="h-4 w-4" aria-hidden="true" />}
        {t(`campaign.socialImport.${action}`)}
      </Button>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5"><Clock3 className={`h-3.5 w-3.5 ${accent}`} aria-hidden="true" />{t('campaign.socialImport.automaticFrequency')}</span>
      <span className="inline-flex items-center gap-1.5"><Mail className={`h-3.5 w-3.5 ${accent}`} aria-hidden="true" />{t('campaign.socialImport.automaticEmail')}</span>
      <span className="inline-flex items-center gap-1.5"><CheckCircle2 className={`h-3.5 w-3.5 ${accent}`} aria-hidden="true" />{t('campaign.socialImport.automaticReview')}</span>
    </div>
    <p id={`${id}-automatic-help`} className="mt-3 text-xs leading-relaxed text-muted-foreground">{t('campaign.socialImport.automaticHelp')}</p>
    <YouTubeDiscoveryNotice allowedPlatforms={props.campaign.allowed_platforms} className="mt-3" />
    {state.preferenceError && <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-destructive">
      <span>{t(state.preferenceError)}</span>
      <Button type="button" variant="ghost" size="sm" disabled={disabled || state.preferenceLoading || state.preferenceSaving}
        onClick={() => void state.retryPreference()}>{t('campaign.socialImport.retry')}</Button>
    </div>}
  </section>;
}
